import {
  HasUserValidPermissionProjectLevelProps,
  RequiredPermissionProjectLevelName,
  RequiredPermissionsProjectLevel,
} from "@utils/permission-control/project-permission-control-types";
import { SdbProject } from "@custom-types/project-types";

/**
 * Object that determines all different permission rules for users to get
 * access on the project level.
 */
export const requiredPermissionsProjectLevel: RequiredPermissionsProjectLevel<RequiredPermissionProjectLevelName> =
  {
    /** Whether the user can change the role of a team member */
    [RequiredPermissionProjectLevelName.canChangeRole]: {
      permissions: ["*", "admin"],
    },

    /** Whether the user can change the group and project manager of a project */
    [RequiredPermissionProjectLevelName.canChangeProjectGroup]: {
      permissions: ["*"],
    },

    /** Whether the user can change the project access level of a project */
    [RequiredPermissionProjectLevelName.canChangeProjectAccessLevel]: {
      permissions: ["*", "admin"],
    },

    /** Whether the user can edit details from a project like name or description */
    [RequiredPermissionProjectLevelName.canEditProjectDetails]: {
      permissions: ["*", "admin"],
    },

    /** Whether the user can view the project cloud activity */
    [RequiredPermissionProjectLevelName.canViewProjectCloudActivity]: {
      permissions: ["*", "admin", "write"],
    },

    /**
     * Whether the user can view the members tab in project overview page
     * and view all users with access to the project.
     * Only project admins (and owners) are allowed; project editors ("write")
     * and viewers ("read") must not be able to access the members list.
     */
    [RequiredPermissionProjectLevelName.canViewAllProjectMembers]: {
      permissions: ["*", "admin"],
    },

    /** Whether the user can view the project settings */
    [RequiredPermissionProjectLevelName.canViewProjectSettings]: {
      permissions: ["*"],
    },

    /** Whether the user can invite members to private project */
    [RequiredPermissionProjectLevelName.canInviteUsersToPrivateProject]: {
      permissions: ["*", "admin"],
    },

    /** Whether the user can invite members to a snapshot */
    [RequiredPermissionProjectLevelName.canInviteMembersToSnapshot]: {
      permissions: ["*", "admin"],
    },

    /** Whether the user can view the project activity */
    [RequiredPermissionProjectLevelName.canEditProjectSettings]: {
      permissions: ["*"],
    },

    /** Whether the user can delete a member from a project */
    [RequiredPermissionProjectLevelName.canDeleteMemberFromProject]: {
      permissions: ["*", "admin"],
    },

    /** Whether the user can delete a project */
    [RequiredPermissionProjectLevelName.canDeleteProject]: {
      permissions: ["*"],
    },

    /** Whether the user can view the markups (annotations) of a project */
    [RequiredPermissionProjectLevelName.canViewProjectMarkups]: {
      permissions: ["*", "admin", "write"],
    },

    /** Whether the user can view the markups (annotations) of a project. Project viewers are allowed */
    [RequiredPermissionProjectLevelName.canViewProjectMarkupsAllowViewers]: {
      permissions: ["*", "admin", "write", "read"],
    },

    /** Whether the user can view the project stats chart in the project overview page */
    [RequiredPermissionProjectLevelName.canViewProjectsStats]: {
      permissions: ["*", "admin"],
    },

    /** Whether the user can view the project data */
    [RequiredPermissionProjectLevelName.canViewProjectData]: {
      permissions: ["*", "admin", "write"],
    },

    /** Whether the user can edit the markup details */
    [RequiredPermissionProjectLevelName.canEditProjectMarkups]: {
      permissions: ["*", "admin", "write"],
    },

    /** Whether the user can view project integrations */
    [RequiredPermissionProjectLevelName.canViewProjectIntegrations]: {
      permissions: ["*", "admin", "write"],
    },
  };

/**
 * Checks whether a user has permissions within the project level.
 *
 * @returns True if the user has a valid project level permission.
 */
export function hasUserValidPermissionProjectLevel<
  PermissionNameT extends string = RequiredPermissionProjectLevelName
>({
  permissionName,
  selectedProject,
  defaultRequiredPermissions = requiredPermissionsProjectLevel as RequiredPermissionsProjectLevel<PermissionNameT>,
}: HasUserValidPermissionProjectLevelProps<PermissionNameT>): boolean {
  if (!selectedProject) {
    return false;
  }

  // Return true if user has all permission, further checking not required
  if (selectedProject.permissions.includes("*")) {
    return true;
  }

  const requiredPermissionsInProject =
    defaultRequiredPermissions[permissionName];
  const requiredPermissions = requiredPermissionsInProject.permissions;

  // If there is no required permissions that means user doesn't required any permission
  if (requiredPermissions.length === 0) {
    return true;
  }

  // Check for the necessary permission to allow users when they have all the required permission in project level
  return requiredPermissions.some((requiredPermission) =>
    selectedProject.permissions.includes(requiredPermission)
  );
}

/**
 * @returns true if the project has only the `describe` permission.
 * Such projects allow the user to view basic project info (name, description,
 * thumbnail, dates) but not open or interact with the project.
 */
export function hasDescribeOnlyPermission(project: SdbProject | null): boolean {
  if (!project) {
    return false;
  }
  return (
    project.permissions.length > 0 &&
    project.permissions.every((p) => p === "describe")
  );
}
